import { motion } from "framer-motion";
import { Building2, MapPin, ArrowRight } from "lucide-react";
import { useExperiences } from "@/hooks/usePortfolio";
import { SectionTitle } from "@/components/SectionTitle";

const formatDate = (d) => {
  if (!d) return "Present";
  const dt = new Date(d);
  return dt.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

export const Experience = () => {
  const { data: experiences, isLoading, error } = useExperiences();

  if (isLoading || error || !experiences) return null;

  const sortedExperiences = [...experiences].sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <section id="experience" className="section-pad relative" data-testid="experience-section">
      <div className="container-x">
        <SectionTitle eyebrow="Journey" title={<>Professional <span className="italic gold-text">experience</span></>} subtitle="A documented progression across India's most respected telecom and technology organisations." />

        <div className="relative max-w-7xl mx-auto">
          {/* Central spine */}
          <div className="hidden md:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px timeline-line" />

          <div className="space-y-10 md:space-y-12">
            {sortedExperiences.map((e, i) => {
              const isLeft = i % 2 === 0;
              return (
                <motion.div
                  key={e.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: 0.05 }}
                  className="relative md:grid md:grid-cols-[1fr_56px_1fr] md:gap-0 items-start"
                  data-testid={`experience-${e.id}`}
                >
                  {/* Marker on spine */}
                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 top-10 z-10 items-center justify-center">
                    <div className="absolute w-10 h-10 rounded-full bg-[hsl(var(--gold))]/15 animate-pulse" />
                    <div className="relative w-4 h-4 rounded-full bg-[hsl(var(--gold))] ring-4 ring-background shadow-[0_0_0_2px_hsl(var(--gold)/0.5)]" />
                  </div>

                  {/* Card */}
                  <div className={`${isLeft ? "md:col-start-1 md:pr-8" : "md:col-start-3 md:pl-8"}`}>
                    <motion.div
                      whileHover={{ y: -4 }}
                      transition={{ duration: 0.35 }}
                      className="glass rounded-2xl p-7 md:p-9 grain relative overflow-hidden"
                    >
                      {/* Connector tab pointing to spine */}
                      <div className={`hidden md:block absolute top-10 ${isLeft ? "right-0 translate-x-full" : "left-0 -translate-x-full"} w-8 h-px bg-gradient-to-${isLeft ? "r" : "l"} from-[hsl(var(--gold))]/60 to-transparent`} />

                      <div className={`flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))] mb-4 ${isLeft ? "md:justify-end" : ""}`}>
                        <span>{formatDate(e.startDate)}</span>
                        <ArrowRight className="w-3 h-3" />
                        <span>{e.currentlyWorking ? "Present" : formatDate(e.endDate)}</span>
                        {e.currentlyWorking && <span className="ml-2 px-2 py-0.5 rounded-full bg-[hsl(var(--gold))]/15 text-[9px] tracking-wider">CURRENT</span>}
                      </div>

                      <h3 className={`font-serif text-2xl md:text-3xl leading-snug ${isLeft ? "md:text-right" : ""}`}>{e.designation}</h3>

                      <div className={`mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-foreground/80 ${isLeft ? "md:justify-end" : ""}`}>
                        <span className="inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-[hsl(var(--gold))]" />{e.companyName}</span>
                        <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[hsl(var(--gold))]" />{e.location}</span>
                      </div>

                      <div className="hairline my-5" />

                      <p className="text-[15px] text-muted-foreground whitespace-pre-line text-justify-luxury">
                        {e.description}
                      </p>
                    </motion.div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
